/*
 *                     NOTICE
 *            COPYRIGHT 2024 Ab Initio
 *       UNPUBLISHED - ALL RIGHTS RESERVED
 *
 * This program is confidential and a trade secret of Ab Initio.
 * The receipt or possession of this program does not convey any
 * rights to reproduce or disclose its contents, or to manufacture,
 * use or sell anything that it may describe, in whole or in part,
 * without the specific written consent of Ab Initio.
 */
import { Expression } from "abreact/expression/Expression";
import { AbstractLeafOperandMultiplexExpressionNodeConverter } from "abreact/expression/ui/editor/impl/model/impl/row/operandValue/converter/multiplex/AbstractLeafOperandMultiplexExpressionNodeConverter";
import { AbstractOperandMultiplexExpressionNodeConverter } from "abreact/expression/ui/editor/impl/model/impl/row/operandValue/converter/multiplex/AbstractOperandMultiplexExpressionNodeConverter";
import { OperatorOperandExpressionNodeConverter } from "abreact/expression/ui/editor/impl/model/impl/row/operandValue/converter/OperatorOperandExpressionNodeConverter";
import { GuidedExpressionEditorUtils } from "abreact/expression/ui/editor/impl/utils/GuidedExpressionEditorUtils";
import { ExpExpressionNodeNodeTypeEnum } from "abweb/generated/abwebinternal/api/dto/ExpExpressionNode";
import { Assert } from "abweb/utils/Assert";
// import CATEGORY_SUB_EXPRESSION = Expression.Domain.Constants.CATEGORY_SUB_EXPRESSION;
// import OperandValue = GuidedExpressionEditor.AbstractModel.AbstractRowModel.OperandValue;

export class AbstractStandardOperandMultiplexExpressionNodeConverter extends AbstractOperandMultiplexExpressionNodeConverter {
  convert(expExpressionNode) {
    switch (expExpressionNode.nodeType) {
      case ExpExpressionNodeNodeTypeEnum.LITERAL:
      case ExpExpressionNodeNodeTypeEnum.REFERENCE:
        return new AbstractLeafOperandMultiplexExpressionNodeConverter(this.specification).convert(expExpressionNode);
      case ExpExpressionNodeNodeTypeEnum.OPERATOR:
        const operatorOperandValue = new OperatorOperandExpressionNodeConverter(this.specification).convert(expExpressionNode);
        if (expExpressionNode.categoryID === Expression.Domain.Constants.CATEGORY_SUB_EXPRESSION) {
          // Sub-expressions serialize the sub-expression into a literal node, but still need to use the created
          // model object to compute if the sub-expression is complete, as that bit is not stored in the node.
          const isComplete = GuidedExpressionEditorUtils.isOperatorComplete(operatorOperandValue);
          return this.convertSubExpressionRootExpExpressionNode(expExpressionNode, isComplete);
        }
        return operatorOperandValue;
      default:
        this.reportUnexpectedNodeType(expExpressionNode);
    }
  }

  /**
   * The inner {@linkcode GuidedRowBasedExpressionEditor} of a sub-expression consumes and produces an
   * {@linkcode ExpExpressionDefinition}. Converting its {@linkcode ExpExpressionDefinition#rootNode} back into an
   * {@linkcode OperatorOperandValue} using {@linkcode OperatorOperandExpressionNodeConverter#convert} incurs overhead
   * since it will eventually be re-exported. But, more importantly, since it is primarily invoked during
   * {@linkcode AbstractModel.Mediator#import}, {@linkcode OperatorOperandExpressionNodeConverter#convert} assumes that
   * the condition {@linkcode OperatorOperandValue}s it recursively produces will populate
   * {@linkcode AbstractRowBasedModel#RowModel}s that have additional exporting logic to handle things like
   * the {@linkcode absentSentinelExpOperator}. Given that an {@link Operand.Value.Editor} may only mutate the
   * {@link AbstractModel} by returning an {@link GuidedExpressionEditor.AbstractModel.AbstractRowModel.OperandValue}, there is no way at this level to create the necessary
   * {@linkcode AbstractRowBasedModel#RowModel}s. To address this, we can instead serialize and store the
   * sub-expression's {@linkcode ExpExpressionDefinition} in a {@linkcode LiteralOperandValue}, capitalizing on the
   * export work that the inner {@linkcode GuidedRowBasedExpressionEditor} has already performed.
   *
   * Doing so simplifies the eventual creation of a unified expression tree across arbitrarily many sub-expressions,
   * because each {@linkcode LiteralOperandValue} that has a {@linkcode AbstractOperandValue#valueCategoryID} of
   * {@linkcode Expression.Domain.Constants.CATEGORY_SUB_EXPRESSION} can then deserialize its stored {@linkcode ExpExpressionDefinition} and replace
   * itself in the tree with the corresponding {@linkcode ExpExpressionDefinition#rootNode}.
   *
   * When this takes place, the {@linkcode LiteralOperandValue} must essentially merge itself with the hoisted
   * {@linkcode ExpExpressionDefinition#rootNode} by populating {@linkcode ExpExpressionNode#operandName},
   * {@linkcode ExpExpressionNode#categoryID} and {@linkcode ExpExpressionNode#valueTypeIDs} with its own values. These
   * values allow {@linkcode AbstractStandardOperandMultiplexExpressionNodeConverter} to recognize sub-expressions in
   * the expression tree and the framework to associate them with the {@linkcode SubExpressionOperandValueEditor}.
   * However, they should be stripped before consumption by the inner {@linkcode GuidedRowBasedExpressionEditor}, since
   * they have no meaning in the inner level.
   *
   * Here, specifically, having recognized a sub-expression {@linkcode ExpExpressionDefinition#rootNode}, we replace it
   * in the expression tree with a counterpart that will be imported as the {@linkcode LiteralOperandValue} holding
   * the serialized {@link ExpExpressionDefinition}. In other words, this is the inverse of the hoisting.
   *
   * @see {@linkcode LiteralOperandValueImpl#toSubExpressionRootExpExpressionNode}
   * @see {@linkcode SubExpressionOperandValueEditor#valueFromProps}
   */
  convertSubExpressionRootExpExpressionNode(expExpressionNode, isComplete) {
    Assert.assert(expExpressionNode.categoryID === Expression.Domain.Constants.CATEGORY_SUB_EXPRESSION);
    const expExpressionDefinition = {
      id: expExpressionNode.id,
      domainID: this.specification.expressionDomainModel.id,
      version: this.specification.expressionDomainModel.version,
      isComplete,
      rootNode: {
        ...expExpressionNode,
        operandName: undefined,
        categoryID: undefined,
        valueTypeIDs: undefined
      }
    };
    return this.convert({
      ...expExpressionNode,
      nodeType: ExpExpressionNodeNodeTypeEnum.LITERAL,
      value: Expression.Definition.serialize(expExpressionDefinition)
    });
  }
}
//# sourceMappingURL=AbstractStandardOperandMultiplexExpressionNodeConverter.js.map
